// The linqset (nodes with their insights) for what the analysis views look at, as the node tables need: the
// organisation's own when every model is looked at, one model's graph when one is, and the organisation's nodes
// totalled over the models when several are. With a project compared to the current state, the current state's too
import { Injectable } from '@angular/core';
import { addNodeInsights, createNewNodeInsights, CURRENT_STATE_PROJECT_KEY, FILTER_MODEL_KEY, Linqset, LINQSET_TYPE, LinqsetNode, ORGANISATION_MODEL_KEY } from '../common/linqset-model';
import { createLinqset, processLinqset } from '../common/model-processing';
import { cloneInsightsfilter, Organisation } from '../common/organisation';
import { LinqsetRepositoryService } from '../model-services/linqset-repository.service';
import { InsightsRefreshService } from '../model-services/insights-refresh.service';
import { AnalysisScope, comparesWithCurrentState, filterForScope } from './scope';

// the organisation's nodes with work in the models given, each with its insights totalled over them (as a 'filter'
// insight), and the same total put on the node in the other linqset so the two can be compared
export const filterNodesForLinqset = (linqset: Linqset, modelKeys: string[], projectKey: string, linqset2?: Linqset): LinqsetNode[] =>
    linqset.nodes.filter(n => {
        // clear out any existing 'filtered' insights
        n.insights = n.insights.filter(i => i.modelKey !== FILTER_MODEL_KEY);
        const modelInsights = n.insights.filter(i => i.projectKey === projectKey && modelKeys.indexOf(i.modelKey) >= 0);
        if (!modelInsights.length) return false;
        const total = modelInsights.reduce((i, mi) => {
            addNodeInsights(mi, i);
            if (mi.isInput) i.isInput = true;
            return i;
        }, createNewNodeInsights(linqset.organisationKey, linqset.workspaceKey, FILTER_MODEL_KEY, n._key, true));
        n.insights.push(total);
        if (linqset2) {
            const n2 = linqset2.getNode(n._key);
            if (n2) n2.insights.push(total);
        }
        return true;
    });

@Injectable({ providedIn: 'root' })
export class ScopeLinqsetService {
    constructor(private linqsetRepo: LinqsetRepositoryService, private refreshes: InsightsRefreshService) {}

    // afresh reads the organisation's nodes again rather than keeping those loaded, for when their insights changed
    async load(organisation: Organisation, scope: AnalysisScope, userKey?: string, afresh = false): Promise<{ linqset: Linqset; comparison?: Linqset }> {
        const filter = filterForScope(scope, userKey);
        // the workspace's insights being worked out again after a change are waited for, and then read afresh
        if (this.refreshes.isRefreshing(scope.workspaceKey)) {
            await this.refreshes.settled(scope.workspaceKey);
            afresh = true;
        }
        // the organisation's nodes for the workspace (and the current state's, to compare a project with)
        await this.linqsetRepo.loadModelData(organisation._key, ORGANISATION_MODEL_KEY, scope.workspaceKey, afresh);
        const organisationLinqset = this.linqsetRepo.organisationLinqset!;
        const currentState = this.linqsetRepo.currentStateOrganisationLinqset;
        const compareFilter = comparesWithCurrentState(scope) ? cloneInsightsfilter(filter, CURRENT_STATE_PROJECT_KEY) : undefined;

        let linqset = createLinqset(organisation, filter);
        let comparison: Linqset | undefined;
        if (linqset.type === LINQSET_TYPE.ORGANISATION) {
            linqset = organisationLinqset;
            if (compareFilter) comparison = currentState;
        } else if (linqset.type === LINQSET_TYPE.MODEL) {
            const graphData = await this.linqsetRepo.loadModelGraph(organisation._key, linqset.modelKey, scope.workspaceKey);
            linqset = createLinqset(organisation, filter, graphData, organisationLinqset.nodes);
            processLinqset(linqset, organisationLinqset, true, organisation);
            if (compareFilter && currentState && linqset.model.projectKey === CURRENT_STATE_PROJECT_KEY) {
                comparison = createLinqset(organisation, compareFilter, graphData, currentState.nodes);
                processLinqset(comparison, currentState, true, organisation);
            }
        } else {
            const nodes = filterNodesForLinqset(organisationLinqset, linqset.modelKeys, linqset.workspaceKey, currentState);
            linqset = createLinqset(organisation, filter, { nodes, edges: organisationLinqset.edges }, []);
            if (compareFilter && currentState) {
                const comparisonNodes = filterNodesForLinqset(currentState, linqset.modelKeys, CURRENT_STATE_PROJECT_KEY, organisationLinqset);
                comparison = createLinqset(organisation, compareFilter, { nodes: comparisonNodes, edges: currentState.edges }, []);
                linqset.model.insights.push(comparison.insights);
                comparison.model.insights.push(linqset.insights);
            }
        }
        linqset.readonlyOverride = true;
        if (comparison) linqset.addRemovedNodesToLinqset(comparison);
        return { linqset, comparison };
    }

    // the current state's roles and systems, when a project was loaded
    get currentStateOrganisationLinqset() {
        return this.linqsetRepo.currentStateOrganisationLinqset;
    }

    get organisationLinqset() {
        return this.linqsetRepo.organisationLinqset;
    }
}
